import React, { useMemo, useState, useEffect } from 'react';
import { withRouter } from 'next/router';
import { connect, useDispatch } from 'react-redux';
import { appQueryParameters } from '@clubmed/utils';
import { setInLocalStorage } from 'Client/local-storage-api';
import { OVER_BOT } from 'Components/frameworks/portal/Portal.constants';
import { ActivitiesContext } from 'Context/ActivitiesContext';
import { usePageContext } from 'Context/PageContext';
import { useAppContext } from 'Context/ReactAppContext';
import { PLE_BOOKING_STAYS_PAGE_ID } from 'Routes/constants';
import { useCurrentUri } from 'Src/hooks/use-current-uri/useCurrentUri';
import { areUpsellsEnabled } from 'Src/hooks/use-stay-settings/useStaySettings.helper';
import { t } from 'Src/i18n';
import { PLE_TRACKING, TRACK_EVENT_NAMES } from 'Tracking/constants';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import Portal from 'Components/frameworks/portal/Portal';
import { BottomNavbarLayer } from 'Components/widgets/bottom-navbar-layer/BottomNavbarLayer';
import {
  fetchBookingsCart,
  updateBookingCart,
  updateBookingCartSequencially,
} from './cart.actions';
import { BookingCart } from './BookingCart';
import { CartPaymentButton } from './CartPaymentButton';
import {
  formatCartBookings,
  formatCartTrackingData,
  formatReservedServicesByAttendee,
} from './BookingCart.utils';
type Props = {
  amount: CartPrice;
  bookings: Array<BookingEntity>;
  cartBookings: Array<Cart>;
  fetchBookingsCart: typeof fetchBookingsCart;
  forwardRef: {
    current: HTMLElement | null | undefined;
  };
  isEmpty: Boolean;
  router: any;
  updateCart: typeof updateBookingCart;
};

const filterAvailableBookings = ({ stays = [] }: BookingEntity, appContext: AppContext): boolean =>
  stays.some((stay) => areUpsellsEnabled(stay, appContext));

type CartBookingItemProps = {
  cartBooking: Cart;
  amount: CartPrice;
  cartBookings: Array<Cart>;
  bookings: Array<BookingEntity>;
  appContext: AppContext;
  dispatch: ReturnType<typeof useDispatch>;
  trackClickEvent: ReturnType<typeof useTrackEvent>['trackClickEvent'];
  reservedServicesByAttendee: unknown;
  shouldShowPaymentButtonOnNavbar: boolean;
  updateCart: typeof updateBookingCart;
};

const CartBookingItem = ({
  cartBooking,
  amount,
  cartBookings,
  bookings,
  appContext,
  dispatch,
  trackClickEvent,
  reservedServicesByAttendee,
  shouldShowPaymentButtonOnNavbar,
  updateCart,
}: CartBookingItemProps) => {
  const stayIndex = cartBooking.stayServices?.[0]?.stayIndex;
  const isCartLocked = !!(cartBooking as Cart & { hasServicesInOption?: boolean })
    .hasServicesInOption;

  const activitiesContextValue = useMemo(
    () =>
      ({
        deleteService: (
          services: ReadonlyArray<UpsellActivity>,
          attendeeIds: ReadonlyArray<string> | string,
        ) => {
          // Block delete when cart is locked due to OPTION services
          if (isCartLocked) {
            return Promise.resolve();
          }

          const isRental = services[0].pricingInfo.type.toLowerCase() === 'rental';
          trackClickEvent(TRACK_EVENT_NAMES.CLICK_CART, {
            detail_click: isRental ? 'remove_ski_equipment' : 'remove_activity',
            ...(isRental
              ? {
                  activity_type: services[0].pricingInfo.type.toLowerCase(),
                }
              : null),
          });
          const cart = services.map((service) => {
            const schedules = service.pricingInfo.schedules
              .map((schedule) => ({
                attendees: (
                  schedule as AdditionalServiceScheduleType & {
                    attendees?: Array<{ id: string; isRented?: boolean }>;
                  }
                ).attendees
                  .filter(({ id, isRented }) => !attendeeIds.includes(id) && isRented)
                  .map(({ id }) => ({
                    id,
                  })),
                end_date: schedule.endDate,
                start_date: schedule.startDate,
              }))
              .filter(({ attendees }) => !!attendees.length);
            return {
              action: schedules.length ? 'update' : 'remove',
              appContext,
              bookingId: cartBooking.bookingId,
              context: appContext,
              id: service.pricingInfo.id,
              schedules,
              serviceName: service.pricingInfo.type,
              stayIndex,
            };
          });
          return updateBookingCartSequencially(cart, {
            amount,
            cartBookings,
          }).finally(() =>
            dispatch(
              fetchBookingsCart(
                bookings.map(({ id }) => id),
                appContext,
              ),
            ),
          );
        },
        attendeesList: [],
        availableActivities: [],
        currency: amount?.total?.currency ?? '',
        trackingData: PLE_TRACKING,
        updateService: async () => undefined,
      }) as unknown as React.ContextType<typeof ActivitiesContext>,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isCartLocked,
      trackClickEvent,
      appContext,
      cartBooking.bookingId,
      stayIndex,
      amount,
      cartBookings,
      bookings,
      dispatch,
    ],
  );

  return (
    <div key={cartBooking.bookingId}>
      <ActivitiesContext.Provider // TODO: cleanup !!
        value={activitiesContextValue}
      >
        <BookingCart
          cart={cartBooking}
          stay={cartBooking?.booking?.stays?.[stayIndex]}
          reservedServicesByAttendee={reservedServicesByAttendee}
          showPaymentButton={!shouldShowPaymentButtonOnNavbar}
          updateCart={updateCart}
          isCartLocked={isCartLocked}
        />
      </ActivitiesContext.Provider>
    </div>
  );
};

const _BookingsCartContainer = ({
  amount,
  bookings,
  cartBookings,
  fetchBookingsCart: dispatchFetchBookingsCart,
  forwardRef,
  isEmpty,
  router,
  updateCart,
}: Props) => {
  const { trackClickEvent } = useTrackEvent();
  const [, setCartOpen] = useState(false);
  const appContext = useAppContext();
  const pageContext = usePageContext();
  const dispatch = useDispatch();
  const currentUri = useCurrentUri();
  const shouldShowPaymentButtonOnNavbar = pageContext.pageId !== PLE_BOOKING_STAYS_PAGE_ID;
  const [reservedServicesByAttendeeMap, setReservedServicesByAttendeeMap] = useState({});
  useEffect(() => {
    setReservedServicesByAttendeeMap(formatReservedServicesByAttendee(cartBookings, appContext));
    setInLocalStorage(PLE_TRACKING, Object.fromEntries(formatCartTrackingData(cartBookings)));
  }, [JSON.stringify(cartBookings.map(({ booking, ...rest }) => ({ ...rest })))]);
  useEffect(() => {
    if (isFeatureFlipped(appContext)('memberSpace.enableBookingCart') && bookings.length > 0) {
      dispatchFetchBookingsCart(
        bookings
          .filter((booking) => filterAvailableBookings(booking, appContext))
          .map(({ id }) => id),
        appContext,
      );
    }
  }, []);

  const closeLayer = () => {
    setCartOpen(false);
    trackClickEvent(TRACK_EVENT_NAMES.CLICK_CART, {
      detail_click: 'continue_shopping',
    });
    const newUri = currentUri
      .clone()
      .removeSearch([appQueryParameters.openLayer, appQueryParameters.catId, 'type'])
      .resource();
    router.replace(newUri);
  };

  if (isEmpty) {
    return (
      <Portal name={OVER_BOT}>
        <div ref={forwardRef as React.Ref<HTMLDivElement>} />
      </Portal>
    );
  }

  return (
    <BottomNavbarLayer
      closeLabel={t('cart.closeCart')}
      closeLayer={closeLayer}
      closeLayerLabel={t('cart.continueShopping')}
      continueBtn={
        <CartPaymentButton
          onClick={() =>
            trackClickEvent(TRACK_EVENT_NAMES.CLICK_CART, {
              detail_click: 'validate_cart_closed',
            })
          }
          stay={bookings[0]?.stays[cartBookings?.[0]?.stayServices?.[0]?.stayIndex]}
          hasOptionServices={
            (cartBookings?.[0] as Cart & { hasServicesInOption?: boolean })?.hasServicesInOption
          }
        />
      }
      continueLabel={t('cart.proceedPayment')}
      forwardRef={forwardRef}
      hideContinueButton={!shouldShowPaymentButtonOnNavbar}
      isPriceLoading={false}
      openLabel={t('cart.openCart')}
      portalPosition={OVER_BOT}
      price={amount}
      showCloseLayerBtn={!!router.query[appQueryParameters.openLayer]}
      trackClickCloseReceipt={() => {
        setCartOpen(false);
        trackClickEvent(TRACK_EVENT_NAMES.CLICK_CART, {
          detail_click: 'close_cart',
        });
      }}
      trackClickOpenReceipt={() => {
        setCartOpen(true);
        trackClickEvent(TRACK_EVENT_NAMES.CLICK_CART, {
          detail_click: 'open_cart',
        });
      }}
    >
      {cartBookings.map((cartBooking) => (
        <CartBookingItem
          key={cartBooking.bookingId}
          cartBooking={cartBooking}
          amount={amount}
          cartBookings={cartBookings}
          bookings={bookings}
          appContext={appContext}
          dispatch={dispatch}
          trackClickEvent={trackClickEvent}
          reservedServicesByAttendee={reservedServicesByAttendeeMap[cartBooking?.bookingId]}
          shouldShowPaymentButtonOnNavbar={shouldShowPaymentButtonOnNavbar}
          updateCart={updateCart}
        />
      ))}
    </BottomNavbarLayer>
  );
};

const mapStateToProps = (state, { bookings = [] }) => {
  const {
    amount = 0,
    cartBookings = [],
    isEmpty = true,
  } = state.memberSpace.cart.data?.bookingsCart || {};
  return {
    amount,
    cartBookings: formatCartBookings(cartBookings, bookings),
    isEmpty,
  };
};

const mapDispatchToProps = {
  fetchBookingsCart,
  updateCart: updateBookingCart,
};
const BookingsCartContainerWithRouter = withRouter(_BookingsCartContainer);
export const BookingsCartContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(BookingsCartContainerWithRouter);
